import { getUrlParameter, updateSearchParam } from 'helpers/core/url';
import { resetUserState } from 'store/userSlice';
import { api } from 'services/api';
import { renderWebClockFailClosed } from '../../../webClockFailClosed';
import { getWebClockUserStorageKey } from './webClockUserStorageKey';
import { getLastKnownOfflineWebClockUserKey } from '../hooks/useOfflineWebClockLifecycle';
import { confirmOfflineWebClockCleanup } from './confirmOfflineWebClockCleanup';
import { setPendingOfflineWebClockClear } from './offlineWebClockStorage';

export const WEBCLOCK_LOGIN_PATH = '/clock/web/login';

export const WEBCLOCK_PATH = '/clock/web';

/** Search param set only after a credential webclock login. */
export const CREDENTIAL_SESSION_PARAM = 'credentialSession';

export const OFFLINE_CLEANUP_GATE_LOG_LABEL =
  '[WebClock] Offline cleanup gate:';

/**
 * True when this clock session was started from the credential login form.
 * URL jwt launches (enclosed, mobile, deep links) do not set the flag.
 *
 * @returns {boolean}
 */
export const isCredentialWebClockSession = () =>
  getUrlParameter(CREDENTIAL_SESSION_PARAM) === 'true';

/**
 * Clears the JWT and credential-session search params and resets RTK Query
 * + user slice state.
 *
 * @param {import('redux').Dispatch} dispatch
 */
export const resetWebClockSession = (dispatch) => {
  updateSearchParam('jwt');
  updateSearchParam(CREDENTIAL_SESSION_PARAM);
  dispatch(api.util.resetApiState());
  dispatch(resetUserState());
};

/**
 * Resolves the offline cleanup user key from Redux user state, falling back
 * to the last-known key captured during offline lifecycle.
 *
 * @param {unknown} user
 * @returns {string | null | undefined}
 */
export const resolveWebClockCleanupUserKey = (user) =>
  getWebClockUserStorageKey(user) || getLastKnownOfflineWebClockUserKey();

/**
 * Fail-closed after offline cleanup already ran and was not confirmed.
 * Does not re-run cleanup (unlike handleWebClockInvalidToken).
 *
 * @param {object} params
 * @param {import('redux').Dispatch} params.dispatch
 * @param {Error} [params.error]
 * @param {string} [params.logLabel]
 */
export const failClosedAfterUnconfirmedOfflineCleanup = ({
  dispatch,
  error,
  logLabel = OFFLINE_CLEANUP_GATE_LOG_LABEL,
}) => {
  resetWebClockSession(dispatch);
  renderWebClockFailClosed(
    error ??
      new Error('Offline secure storage was not cleared after session ended.'),
    logLabel
  );
};

/**
 * Resets session state and navigates to the WebClock login route.
 *
 * @param {object} params
 * @param {import('redux').Dispatch} params.dispatch
 * @param {{ replace: (path: string) => void }} params.history
 */
export const completeWebClockSignOut = ({ dispatch, history }) => {
  resetWebClockSession(dispatch);
  history.replace(WEBCLOCK_LOGIN_PATH);
};

/**
 * Handles JWT invalidation: confirm offline cleanup, then either fail-closed
 * or complete sign-out to login.
 *
 * @param {object} params
 * @param {import('redux').Dispatch} params.dispatch
 * @param {() => unknown} params.getUser
 * @param {{ replace: (path: string) => void }} params.history
 * @returns {Promise<void>}
 */
export const handleWebClockInvalidToken = async ({
  dispatch,
  getUser,
  history,
}) => {
  const userKey = resolveWebClockCleanupUserKey(getUser());
  const { cleared, pendingKey } = await confirmOfflineWebClockCleanup(userKey);

  if (!cleared) {
    setPendingOfflineWebClockClear(pendingKey);
    failClosedAfterUnconfirmedOfflineCleanup({
      dispatch,
      error: new Error(
        'Offline secure storage was not cleared after token invalidation.'
      ),
    });
    return;
  }

  completeWebClockSignOut({ dispatch, history });
};

/**
 * Confirms offline cleanup during explicit sign-out / inactivity timeout.
 * On failure, records a pending clear and fail-closes; on success, signs out.
 *
 * @param {object} params
 * @param {import('redux').Dispatch} params.dispatch
 * @param {{ replace: (path: string) => void }} params.history
 * @param {unknown} [params.user]
 * @returns {Promise<void>}
 */
export const handleWebClockLogout = async ({ dispatch, history, user }) => {
  const cleanupKey = resolveWebClockCleanupUserKey(user);
  const { cleared, pendingKey } = await confirmOfflineWebClockCleanup(
    cleanupKey
  );

  if (!cleared) {
    setPendingOfflineWebClockClear(pendingKey);
    failClosedAfterUnconfirmedOfflineCleanup({
      dispatch,
      error: new Error(
        'Offline secure storage was not cleared during sign out.'
      ),
    });
    return;
  }

  completeWebClockSignOut({ dispatch, history });
};
